import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCalendarEvents } from '@/modules/calendar/services';
import type {
  CalendarEvent,
  CalendarEventFilters,
  VisibleDateRange,
} from '@/modules/calendar/types';

type UseCalendarEventsArgs = {
  visibleRange: VisibleDateRange;
  filters: CalendarEventFilters;
};

export function useCalendarEvents({ visibleRange, filters }: UseCalendarEventsArgs) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stableFilters = useMemo((): CalendarEventFilters => {
    const next: CalendarEventFilters = {
      showReservations: filters.showReservations !== false,
      showLogistics: filters.showLogistics !== false,
      showMaintenance: filters.showMaintenance !== false,
    };
    if (filters.status) next.status = filters.status;
    if (filters.itemId) next.itemId = filters.itemId;
    if (filters.categoryId) next.categoryId = filters.categoryId;
    return next;
  }, [
    filters.status,
    filters.itemId,
    filters.categoryId,
    filters.showReservations,
    filters.showLogistics,
    filters.showMaintenance,
  ]);

  const queryKey = `${visibleRange.rangeStart}|${visibleRange.rangeEnd}|${stableFilters.status ?? ''}|${stableFilters.itemId ?? ''}|${stableFilters.categoryId ?? ''}`;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCalendarEvents({
        rangeStart: visibleRange.rangeStart,
        rangeEnd: visibleRange.rangeEnd,
        filters: stableFilters,
      });
      setEvents(data);
    } catch {
      setError('No se pudieron cargar los eventos de la agenda.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [visibleRange.rangeStart, visibleRange.rangeEnd, stableFilters]);

  useEffect(() => {
    void refresh();
  }, [refresh, queryKey]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      let cursor = event.start;
      while (cursor <= event.end) {
        const list = map.get(cursor) ?? [];
        list.push(event);
        map.set(cursor, list);
        const next = new Date(`${cursor}T12:00:00`);
        next.setDate(next.getDate() + 1);
        const y = next.getFullYear();
        const m = String(next.getMonth() + 1).padStart(2, '0');
        const d = String(next.getDate()).padStart(2, '0');
        cursor = `${y}-${m}-${d}`;
      }
    }
    return map;
  }, [events]);

  return { events, eventsByDay, loading, error, refresh };
}
